/**
 * PermissionGate — dwa sposoby bramkowania interfejsu uprawnieniami (F24 Kawałek 1).
 *
 * Decyzja właściciela rozdziela dwa przypadki, bo znaczą dla użytkownika co innego:
 *
 *   `<Can>`              — czego NIE WIDZISZ, tego nie ma na ekranie. Dla wejść do funkcji:
 *                          pozycji menu, zakładek, linków. Pokazywanie wejścia, które i tak
 *                          odmówi, to obietnica bez pokrycia.
 *
 *   `<PermissionButton>` — czego NIE MOŻESZ ZROBIĆ, to widzisz jako nieaktywne, a po kliknięciu
 *                          dostajesz wyjaśnienie. Dla akcji na rekordzie: „Edytuj", „Usuń".
 *                          Zniknięcie przycisku zostawiłoby użytkownika z pytaniem „czy tej
 *                          funkcji nie ma, czy nie mam do niej prawa" — a to pierwsze zgłoszenie,
 *                          które trafia na wsparcie.
 *
 * Obie bramki są PODPOWIEDZIĄ, nie kontrolą dostępu: rozstrzyga serwer przy każdym żądaniu.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, type ButtonProps } from '@/components/Button';
import { useToast } from '@/contexts/ToastContext';
import { usePermission } from '@/hooks/usePermission';

export interface CanProps {
  /** Kod uprawnienia, np. `documents.purchase_invoice.view`. */
  code: string;
  children: ReactNode;
  /** Co pokazać zamiast — domyślnie nic. */
  fallback?: ReactNode;
}

/** Chowa zawartość, gdy brak uprawnienia. */
export function Can({ code, children, fallback = null }: CanProps): ReactNode {
  const { allowed } = usePermission(code);
  return allowed ? <>{children}</> : <>{fallback}</>;
}

interface PermissionButtonBaseProps extends Omit<ButtonProps, 'onClick'> {
  onClick?: () => void;
}

/** Wariant z kodem uprawnienia — prawo liczy `usePermission` (uprawnienia modułu / kartoteki). */
export interface PermissionButtonCodeProps extends PermissionButtonBaseProps {
  /** Kod uprawnienia wymagany do wykonania akcji. */
  code: string;
  allowed?: never;
  reason?: never;
}

/**
 * Wariant z prawem podanym z zewnątrz — dla akcji, o których rozstrzyga odpowiedź serwera dla
 * konkretnego rekordu (np. `permissions.canEdit` karty), a nie sam kod uprawnienia.
 */
export interface PermissionButtonExplicitProps extends PermissionButtonBaseProps {
  /** Czy akcja jest dozwolona. */
  allowed: boolean;
  /** Wyjaśnienie braku prawa (podpowiedź pod kursorem i komunikat po kliknięciu). */
  reason: string;
  code?: never;
}

export type PermissionButtonProps = PermissionButtonCodeProps | PermissionButtonExplicitProps;

interface GatedButtonProps extends PermissionButtonBaseProps {
  allowed: boolean;
  reason: string;
}

/**
 * Przycisk akcji, który przy braku prawa zostaje WIDOCZNY, ale nieaktywny — z podpowiedzią
 * pod kursorem i komunikatem po kliknięciu.
 *
 * Nieaktywny przycisk nie wywołuje zdarzenia kliknięcia, więc komunikat wiesza się na otoczce.
 * Bez tego użytkownik klika w martwy element i nie dowiaduje się, dlaczego nic się nie dzieje.
 */
function GatedButton({
  allowed,
  reason,
  onClick,
  disabled,
  children,
  ...rest
}: GatedButtonProps): ReactNode {
  const toast = useToast();

  if (allowed) {
    return (
      <Button {...rest} disabled={disabled} onClick={onClick}>
        {children}
      </Button>
    );
  }

  return (
    <span
      title={reason}
      onClick={() => toast.show({ variant: 'warning', title: reason })}
      // Otoczka przejmuje kliknięcie za nieaktywny przycisk — sam `disabled` zdarzenia nie emituje.
      role="presentation"
      className="inline-flex"
    >
      <Button {...rest} disabled aria-describedby={undefined} title={reason}>
        {children}
      </Button>
    </span>
  );
}

function CodePermissionButton({ code, ...rest }: PermissionButtonCodeProps): ReactNode {
  const { t } = useTranslation();
  const { allowed, reasonKey } = usePermission(code);
  const reason = t(reasonKey ?? 'tenant.permissions.denied.tooltip');
  return <GatedButton {...rest} allowed={allowed} reason={reason} />;
}

/**
 * Przycisk akcji z bramką uprawnień: albo `code` (prawo liczy `usePermission`), albo `allowed` +
 * `reason` (prawo podane z zewnątrz, np. z odpowiedzi serwera dla rekordu). Typ pilnuje, że jest
 * dokładnie jeden z dwóch wariantów.
 */
export function PermissionButton(props: PermissionButtonProps): ReactNode {
  if (props.allowed !== undefined) {
    return <GatedButton {...props} />;
  }
  return <CodePermissionButton {...props} />;
}
